import { ContentCopy, Email, Link as LinkIcon, Person, Save } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import userRegistryApi from '../api/userRegistryApi';
import useUserAuth from '../hooks/useUserAuth';
import { useNotify } from './NotificationProvider';

const UserProfileDialog = ({ open, onClose }) => {
  const { showMessage } = useNotify();
  const { currentUser } = useUserAuth();
  const [loading, setLoading] = useState(true); // Start as true to prevent flash
  const [profileData, setProfileData] = useState({
    userSignature: '',
    bookingLink: '',
    // Add other user-specific variables as needed
  });

  // Load user profile data on open
  useEffect(() => {
    if (open && currentUser?.email) {
      setLoading(true);
      loadUserProfile();
    }
  }, [open, currentUser?.email]);

  const loadUserProfile = async () => {
    if (!currentUser?.email) {
      console.warn('No user email available for loading profile');
      return;
    }

    setLoading(true);
    try {
      console.log('🔵 Loading profile for:', currentUser.email);
      
      // Load from user account via API
      const profileSettings = await userRegistryApi.getUserProfile(currentUser.email);
      
      console.log('🟢 Profile loaded:', profileSettings);
      
      // Only update state after data is loaded (prevents flash of empty fields)
      setProfileData({
        userSignature: profileSettings.userSignature || '',
        bookingLink: profileSettings.bookingLink || '',
      });
      setLoading(false);
    } catch (error) {
      console.error('🔴 Error loading user profile:', error);
      setLoading(false);
      
      // Show a more helpful message if user doesn't exist
      if (error.message.includes('User not found') || error.message.includes('User account not found')) {
        showMessage('First time setup - please enter your profile details', { severity: 'info' });
      } else {
        showMessage('Error loading user profile', { severity: 'warning' });
      }
      
      // Only reset to empty if there's an error (keeps previous values if API fails)
      setProfileData({
        userSignature: '',
        bookingLink: '',
      });
    }
  };

  const saveUserProfile = async () => {
    if (!currentUser?.email) {
      showMessage('Error: No user account found', { severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      console.log('🔵 Attempting to save profile for:', currentUser.email);
      console.log('🔵 Profile data:', profileData);
      
      // Save to user account via API
      const savedSettings = await userRegistryApi.saveUserProfile(currentUser.email, profileData);
      
      console.log('🟢 Profile saved successfully:', savedSettings);
      showMessage('User profile saved successfully', { severity: 'success' });
      onClose();
    } catch (error) {
      console.error('🔴 Error saving user profile:', error);
      const errorMessage = error.message || 'Unknown error occurred';
      showMessage(`Error saving profile: ${errorMessage}`, { 
        severity: 'error',
        persist: true 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const copyToClipboard = async text => {
    try {
      await navigator.clipboard.writeText(text);
      showMessage('Copied to clipboard', { severity: 'success' });
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const templateVariables = [
    {
      variable: '{{userSignature}}',
      field: 'userSignature',
      label: 'Email Signature',
      description: 'Your personal email signature block',
      icon: <Email />,
      placeholder:
        'Best regards,\\nJohn Doe\\nSenior Account Manager\\nCondo Shield\\n(555) 123-4567\\njohn@condoshield.org',
    },
    {
      variable: '{{bookingLink}}',
      field: 'bookingLink',
      label: 'Booking Link',
      description: 'Your personal calendar booking URL',
      icon: <LinkIcon />,
      placeholder: 'https://calendly.com/your-username/15min',
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Person color='primary' />
        User Profile Settings
      </DialogTitle>

      <DialogContent>
        <Alert severity='info' sx={{ mb: 3 }}>
          <Typography variant='body2'>
            Configure your personal template variables here. These values will be used when creating
            email templates and communication logs.
          </Typography>
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {templateVariables.map(variable => {
            const currentValue = profileData[variable.field] || '';
            const isEmpty = !currentValue.trim();

            return (
              <Box key={variable.field}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {variable.icon}
                  <Typography variant='h6' sx={{ fontWeight: 600 }}>
                    {variable.label}
                  </Typography>
                  <Chip
                    label={variable.variable}
                    variant='outlined'
                    size='small'
                    sx={{
                      fontFamily: 'monospace',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'primary.main', color: 'white' },
                    }}
                    onClick={() => copyToClipboard(variable.variable)}
                  />
                  <Tooltip title='Copy variable to clipboard'>
                    <IconButton size='small' onClick={() => copyToClipboard(variable.variable)}>
                      <ContentCopy fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                  {variable.description}
                </Typography>

                <TextField
                  fullWidth
                  multiline={variable.field === 'userSignature'}
                  rows={variable.field === 'userSignature' ? 4 : 1}
                  value={currentValue}
                  onChange={e => handleInputChange(variable.field, e.target.value)}
                  placeholder={loading ? 'Loading...' : variable.placeholder}
                  disabled={loading}
                  error={isEmpty && !loading}
                  helperText={
                    isEmpty && !loading ? `${variable.label} is not set - template variable will be empty` : ''
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: isEmpty && !loading ? 'rgba(255, 152, 0, 0.05)' : 'inherit',
                    },
                  }}
                />

                {!isEmpty && (
                  <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ display: 'block', mb: 1 }}
                    >
                      Preview:
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: variable.field === 'userSignature' ? 'inherit' : 'monospace',
                      }}
                    >
                      {currentValue}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ mt: 2 }} />
              </Box>
            );
          })}
        </Box>

        <Alert severity='info' sx={{ mt: 3 }}>
          <Typography variant='body2'>
            <strong>Note:</strong> These settings are saved to your user account and will be
            synchronized across all your devices and sessions.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={saveUserProfile}
          variant='contained'
          startIcon={<Save />}
          disabled={loading}
          sx={{ color: 'white' }}
        >
          Save Profile
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserProfileDialog;
